// src/services/authedFetch.js
//
// fetch() that proves who is calling (D-1 stages 3 and 4).
//
// The Cloud Functions behind this app are public HTTPS addresses, compiled into
// the JavaScript every visitor downloads. Several of them used to act on any
// request that arrived — sending a text from the clinic's number, emailing from
// the clinic's address, spending money on an AI call. The pages calling them
// already knew who their user was; they simply never said so.
//
// Every page using this sits behind RequireAuth, which does not render its
// children until Firebase auth has resolved, so `auth.currentUser` is populated
// by the time any of this runs. That is the same assumption six other services
// in this directory already make.
//
// sheetPatientsService.js deliberately does NOT use this helper: it must stay
// free of a top-level firebase import so its unit tests can run without
// initialising the Firebase app, so it resolves headers lazily instead.

import { auth } from "../firebase";

/** Thrown when there is no signed-in user to authenticate the call with. */
export class NotSignedInError extends Error {
  constructor(message = "You must be signed in to do that.") {
    super(message);
    this.name = "NotSignedInError";
  }
}

/**
 * Same signature as fetch(), plus an Authorization header carrying the signed-in
 * user's Firebase ID token. Any headers supplied by the caller are preserved.
 *
 * Throws BEFORE issuing the request if nobody is signed in, so an unauthenticated
 * caller never reaches the endpoint.
 *
 * @param {string} url
 * @param {object} [init] standard fetch init
 * @returns {Promise<Response>}
 * @throws  {NotSignedInError}
 */
export async function authedFetch(url, init = {}) {
  const user = auth.currentUser;
  if (!user) throw new NotSignedInError();
  const idToken = await user.getIdToken();

  return fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${idToken}`,
    },
  });
}
